import {
  buildAddressForAgentRequest,
  normalizePin,
} from "./catalogAgentRequest.js";
import { isAzerbaijanMobilePhone } from "./catalogPhone.js";

const ADULT_AGE_YEARS = 18;

function nonEmptyString(value) {
  return value != null && String(value).trim() !== "";
}

/** Full years from date of birth; null if DOB is missing or invalid. */
export function getPersonAgeYears(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = dateOfBirth instanceof Date ? new Date(dateOfBirth.getTime()) : new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const hadBirthday =
    today.getMonth() > dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());
  if (!hadBirthday) age -= 1;
  return age;
}

export function isPersonAdult(dateOfBirth, minAge = ADULT_AGE_YEARS) {
  const age = getPersonAgeYears(dateOfBirth);
  return age != null && age >= minAge;
}

/** Display: Soyad Ad Ata */
export function formatQueuedPersonName(values) {
  const parts = [values?.lastName, values?.firstName, values?.middleName]
    .map((s) => (s != null ? String(s).trim() : ""))
    .filter(Boolean);
  return parts.join(" ") || "—";
}

export function formatQueuedPersonBirthDate(dateOfBirth) {
  if (!dateOfBirth) return "—";
  const d = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

export function createQueuedPerson(values, searchedPin, isAsan = false) {
  const pin = normalizePin(searchedPin ?? values?.pin);
  return {
    id: crypto.randomUUID(),
    pin,
    searchedPin: pin,
    isAsan: isAsan === true,
    values: { ...values, pin },
  };
}

export function findQueuedPersonIndex(queue, pin) {
  const normalized = normalizePin(pin);
  if (!normalized) return -1;
  return queue.findIndex((item) => normalizePin(item.pin) === normalized);
}

export function upsertQueuedPerson(queue, values, searchedPin, isAsan = false) {
  const entry = createQueuedPerson(values, searchedPin, isAsan);
  const idx = findQueuedPersonIndex(queue, entry.pin);
  if (idx >= 0) {
    const next = [...queue];
    next[idx] = { ...entry, id: queue[idx].id };
    return { queue: next, entry: next[idx], updated: true };
  }
  return { queue: [...queue, entry], entry, updated: false };
}

export function removeQueuedPerson(queue, id) {
  return queue.filter((item) => item.id !== id);
}

export function updateQueuedPerson(queue, id, values, searchedPin, isAsan = false) {
  const pin = normalizePin(searchedPin ?? values?.pin);
  return (queue || []).map((item) => {
    if (item.id !== id) return item;
    return {
      ...item,
      pin,
      searchedPin: pin,
      isAsan: isAsan === true,
      values: { ...values, pin },
    };
  });
}

export function findQueuedPersonPinConflict(queue, pin, excludeId = null) {
  const normalized = normalizePin(pin);
  if (!normalized) return null;
  return (
    (queue || []).find(
      (item) => item.id !== excludeId && normalizePin(item.pin) === normalized
    ) ?? null
  );
}

export function buildQueuedValuesFromDraft(draft, mainValues = {}, options = {}) {
  const pin = normalizePin(draft?.pin);
  const merged = {
    pin,
    lastName: draft?.lastName != null ? String(draft.lastName).trim() : "",
    firstName: draft?.firstName != null ? String(draft.firstName).trim() : "",
    middleName: draft?.middleName != null ? String(draft.middleName).trim() : "",
    mobilePhone: draft?.mobilePhone != null ? String(draft.mobilePhone).trim() : "",
  };

  const cached = options.searchCache?.[pin];
  if (cached) {
    return { ...cached, ...merged, pin };
  }

  const mainPin = normalizePin(options.mainSearchPin);
  if (
    options.personSearchDone &&
    mainPin &&
    mainPin === pin &&
    mainValues &&
    typeof mainValues === "object"
  ) {
    return { ...mainValues, ...merged, pin };
  }

  return {
    sex: mainValues?.sex || "m",
    townId: mainValues?.townId ?? null,
    ...merged,
  };
}

export function canAddToQueue(draft) {
  return isQueuedPersonValid(draft);
}

function isValidEmail(value) {
  const email = value != null ? String(value).trim() : "";
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** PIN / IdSeries / IdNumber optional (manual entries); ASAN fills them automatically. */
export function isQueuedPersonValid(values) {
  if (!buildAddressForAgentRequest(values)) return false;
  if (!nonEmptyString(values?.lastName)) return false;
  if (!nonEmptyString(values?.firstName)) return false;
  if (!nonEmptyString(values?.middleName)) return false;
  if (!isValidEmail(values?.email)) return false;
  if (!nonEmptyString(values?.mobilePhone)) return false;
  if (nonEmptyString(values?.mobilePhone) && !isAzerbaijanMobilePhone(values?.mobilePhone)) {
    return false;
  }
  return true;
}

export function splitQueueByMain(queue, mainInsuredId) {
  const main = (queue || []).find((person) => person.id === mainInsuredId) || null;
  const cards = (queue || []).filter((person) => person.id !== mainInsuredId);
  return { main, cards };
}

export function buildCardsExcludingMainPin(queue, mainPin) {
  const normalized = normalizePin(mainPin);
  if (!normalized) return [...(queue || [])];
  return (queue || []).filter((person) => normalizePin(person.pin) !== normalized);
}

export function createFormMainInsured(values, searchedPin, isAsan = false) {
  const pin = normalizePin(searchedPin ?? values?.pin);
  return {
    values: { ...values, pin },
    searchedPin: pin,
    isAsan: isAsan === true,
  };
}

export function resolveMainInsuredIdAfterRemove(queue, removedId, currentMainId) {
  if (currentMainId !== removedId) return currentMainId;
  return null;
}
